// Reusable accent-coloured button styles for CTAs across the site. Navy/red are
// still the primary voice; these let a button pick up a topic-family accent
// (teal / slate / coral / forest / burgundy / gold) or sky.
//
// IMPORTANT: the class strings below are LITERAL so Tailwind's JIT scanner
// generates them. Never build `bg-brand-${x}` dynamically — it won't compile.
//
// `solid` fills with the accent and carries white text. Gold fills with a
// deeper gold (brand-gold-deep) so white clears WCAG AA — the lighter brand
// gold is too pale under white. `outline` shows an accent border that fills on
// hover. On the navy hero/flagship only the lighter accents (gold, sky) hold
// contrast — use those there; the darker ones are for buttons on the cream
// surface. (The gold OUTLINE still uses navy text on a light hover fill.)

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-smooth font-heading font-bold transition-colors";

const SIZE = {
  sm: "text-body-sm px-4 py-2",
  md: "text-cta px-6 py-3.5",
  lg: "text-cta px-7 py-4",
} as const;

const SOLID = {
  navy: "bg-brand-navy text-white hover:bg-brand-navy/90",
  red: "bg-brand-red text-white hover:bg-brand-red/90",
  teal: "bg-brand-teal text-white hover:bg-brand-teal/90",
  slate: "bg-brand-slate text-white hover:bg-brand-slate/90",
  coral: "bg-brand-coral text-white hover:bg-brand-coral/90",
  forest: "bg-brand-forest text-white hover:bg-brand-forest/90",
  burgundy: "bg-brand-burgundy text-white hover:bg-brand-burgundy/90",
  gold: "bg-brand-gold-deep text-white hover:bg-brand-gold-deep/90",
  sky: "bg-brand-sky text-white hover:bg-brand-sky/90",
} as const;

const OUTLINE = {
  navy: "border border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white",
  red: "border border-brand-red text-brand-red hover:bg-brand-red hover:text-white",
  teal: "border border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white",
  coral: "border border-brand-coral text-brand-coral hover:bg-brand-coral hover:text-white",
  forest: "border border-brand-forest text-brand-forest hover:bg-brand-forest hover:text-white",
  burgundy: "border border-brand-burgundy text-brand-burgundy hover:bg-brand-burgundy hover:text-white",
  gold: "border border-brand-gold text-brand-navy hover:bg-brand-gold",
  sky: "border border-brand-sky text-brand-sky hover:bg-brand-sky hover:text-white",
} as const;

export type ButtonSolidAccent = keyof typeof SOLID;
export type ButtonOutlineAccent = keyof typeof OUTLINE;
export type ButtonSize = keyof typeof SIZE;

/** Filled accent button classes, e.g. btnSolid("teal") or btnSolid("gold", "lg"). */
export function btnSolid(accent: ButtonSolidAccent, size: ButtonSize = "md"): string {
  return `${BASE} ${SIZE[size]} ${SOLID[accent]}`;
}

/** Outline accent button classes that fill on hover. */
export function btnOutline(accent: ButtonOutlineAccent, size: ButtonSize = "md"): string {
  return `${BASE} ${SIZE[size]} ${OUTLINE[accent]}`;
}
